/**
 * Personnalisation de la page de tarifs selon l'intention de l'apprenant.
 *
 * L'onboarding demande déjà « pourquoi êtes-vous là » (InterestStep → category,
 * GoalStep → goal). Ces réponses ne servaient qu'au dashboard : ce module les
 * réutilise pour adapter le hero, l'ordre des arguments et le parcours affiché
 * sur /pricing.
 *
 * Les ~20 objectifs sont regroupés en 5 familles d'intention pour garder une
 * copy maintenable. Ordre de résolution :
 *   goal (onboarding) → cookie du sélecteur → category (onboarding) → discover.
 *
 * Module pur (aucun I/O) : utilisable côté serveur comme côté client, et
 * réutilisable tel quel par un futur paywall in-app.
 */

import {
    buildLearningPath,
    getAvailableCourses,
    getCourseTitle,
} from "./courses-catalog";
import { getGoalLabelKey, getTargetSlugForProfile } from "./recommendations";

/** Nom du cookie posé par le sélecteur d'intention (90 jours). */
export const PRICING_INTENT_COOKIE = "believemy_pricing_intent";
export const PRICING_INTENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

/**
 * Familles d'intention. « discover » est l'état par défaut : la page reste
 * alors strictement identique à sa version générique.
 */
export const PRICING_INTENTS = [
    "career",
    "build",
    "business",
    "automate",
    "discover",
];

/** Familles proposées dans le sélecteur (discover en est absent : c'est le défaut). */
export const PRICING_SELECTABLE_INTENTS = [
    "career",
    "build",
    "business",
    "automate",
];

/** Objectif d'onboarding → famille d'intention. */
const GOAL_TO_INTENT = {
    // career
    "get-tech-job": "career",
    "ai-for-job": "career",
    "become-freelance": "career",
    // build
    "create-first-website": "build",
    "develop-mobile-app": "build",
    "launch-saas": "build",
    "put-site-online": "build",
    // business
    "launch-business": "business",
    "grow-business": "business",
    "nocode-business": "business",
    // automate
    "automate-tasks": "automate",
    "create-ai-agents": "automate",
    "connect-tools": "automate",
    "daily-ai": "automate",
    "code-with-ai": "automate",
    "ai-automations": "automate",
    // discover (exploration assumée : on ne promet rien de ciblé)
    "discover-programming": "discover",
    "discover-nocode": "discover",
    "discover-ai": "discover",
    "explore-all": "discover",
    "dont-know": "discover",
};

/**
 * Intérêt d'onboarding → famille d'intention.
 * Filet pour les comptes antérieurs au 09/04/2026 : ils ont un category mais
 * pas de goal, la colonne n'ayant jamais été rétro-remplie.
 */
const CATEGORY_TO_INTENT = {
    developers: "build",
    nocode: "automate",
    ai: "automate",
    entrepreneurs: "business",
    explorer: "discover",
};

/**
 * Formation cible par famille, quand aucun objectif précis n'est connu
 * (visiteur anonyme ayant cliqué une pastille, ou compte sans goal).
 */
const INTENT_TARGET_SLUG = {
    career: "formation-nextjs",
    build: "formation-nextjs",
    business: "formation-n8n",
    automate: "formation-n8n",
};

/** Libellé court de chaque famille, utilisé par le sélecteur et le parcours. */
export const INTENT_LABEL_KEYS = {
    career: "pricing_intent_career",
    build: "pricing_intent_build",
    business: "pricing_intent_business",
    automate: "pricing_intent_automate",
    discover: "pricing_intent_discover",
};

/** Icône Solar de chaque pastille du sélecteur. */
export const INTENT_ICONS = {
    career: "case-minimalistic-bold-duotone",
    build: "rocket-2-bold-duotone",
    business: "shop-bold-duotone",
    automate: "bolt-bold-duotone",
};

/** Hero par famille ; discover conserve les clés historiques. */
const HERO_KEYS = {
    career: {
        title: "pricing_hero_title_career",
        subtitle: "pricing_hero_subtitle_career",
    },
    build: {
        title: "pricing_hero_title_build",
        subtitle: "pricing_hero_subtitle_build",
    },
    business: {
        title: "pricing_hero_title_business",
        subtitle: "pricing_hero_subtitle_business",
    },
    automate: {
        title: "pricing_hero_title_automate",
        subtitle: "pricing_hero_subtitle_automate",
    },
    discover: {
        title: "pricing_hero_title",
        subtitle: "pricing_hero_subtitle",
    },
};

/**
 * Les cinq arguments de la carte Premium, par identifiant stable.
 * « courses » attend un paramètre numberOfCourses à l'affichage.
 */
export const PREMIUM_FEATURE_KEYS = {
    courses: "pricing_premium_access_to_courses",
    reps: "pricing_premium_reps_unlimited",
    exercises: "pricing_premium_advanced_exercises",
    certificates: "pricing_premium_success_certificates",
    support: "pricing_premium_personalized_support",
};

/**
 * Ordre des arguments par famille, et réécriture des deux premiers pour qu'ils
 * parlent de l'objectif plutôt que du produit. Les trois suivants gardent leur
 * libellé générique : au-delà du deuxième, on lit de moins en moins.
 */
const FEATURE_PLANS = {
    career: {
        order: ["certificates", "exercises", "courses", "support", "reps"],
        labels: {
            certificates: "pricing_premium_career_certificates",
            exercises: "pricing_premium_career_exercises",
        },
    },
    build: {
        order: ["courses", "exercises", "support", "reps", "certificates"],
        labels: {
            courses: "pricing_premium_build_courses",
            exercises: "pricing_premium_build_exercises",
        },
    },
    business: {
        order: ["support", "courses", "reps", "exercises", "certificates"],
        labels: {
            support: "pricing_premium_business_support",
            courses: "pricing_premium_business_courses",
        },
    },
    automate: {
        order: ["courses", "reps", "exercises", "support", "certificates"],
        labels: {
            courses: "pricing_premium_automate_courses",
            reps: "pricing_premium_automate_reps",
        },
    },
    discover: {
        order: ["courses", "reps", "exercises", "certificates", "support"],
        labels: {},
    },
};

/** Vrai si la valeur est une famille d'intention connue. */
export function isPricingIntent(value) {
    return PRICING_INTENTS.includes(value);
}

/**
 * Objectifs d'onboarding appartenant à une famille.
 * Sert à pré-sélectionner GoalStep pour qui a déjà répondu sur /pricing :
 * la question reste posée, mais la réponse cohérente est déjà cochée.
 */
export function getGoalsForIntent(intent) {
    return Object.keys(GOAL_TO_INTENT).filter(
        (goal) => GOAL_TO_INTENT[goal] === intent,
    );
}

/**
 * Résout la famille d'intention et sa provenance.
 * @returns {{ intent: string, source: "goal"|"selector"|"category"|"none" }}
 */
export function resolvePricingIntent({ goal, category, cookieIntent } = {}) {
    if (goal && GOAL_TO_INTENT[goal]) {
        return { intent: GOAL_TO_INTENT[goal], source: "goal" };
    }
    if (isPricingIntent(cookieIntent)) {
        return { intent: cookieIntent, source: "selector" };
    }
    if (category && CATEGORY_TO_INTENT[category]) {
        return { intent: CATEGORY_TO_INTENT[category], source: "category" };
    }
    return { intent: "discover", source: "none" };
}

/**
 * Nombre de formations annoncé sur la page (variable inlinée au build).
 * Le catalogue sert de filet si la variable n'est pas définie.
 */
function getAnnouncedCatalogSize() {
    return (
        Number(process.env.NUMBER_OF_COURSES) || getAvailableCourses().length
    );
}

/** Liste ordonnée des arguments Premium pour une famille. */
export function getPremiumFeatures(intent) {
    const plan = FEATURE_PLANS[intent] || FEATURE_PLANS.discover;
    return plan.order.map((id) => ({
        id,
        labelKey: plan.labels[id] || PREMIUM_FEATURE_KEYS[id],
    }));
}

/**
 * Parcours de formations à afficher : celui de l'objectif quand il est connu,
 * sinon celui de la famille. Les titres sont résolus dans la locale demandée.
 */
function buildPath({ goal, category, intent, locale }) {
    if (intent === "discover") return [];

    const targetSlug =
        goal && GOAL_TO_INTENT[goal]
            ? getTargetSlugForProfile(category, goal)
            : INTENT_TARGET_SLUG[intent];

    if (!targetSlug) return [];

    return buildLearningPath(targetSlug).map((course) => ({
        slug: course.slug,
        title: getCourseTitle(course, locale),
        image: course.image,
        duration: course.duration,
    }));
}

/**
 * Construit tout ce dont /pricing a besoin pour se personnaliser.
 * Appelé côté serveur (page.jsx) pour un rendu sans scintillement, et côté
 * client quand le sélecteur change l'intention.
 */
export function buildPricingPersonalization({
    goal,
    category,
    cookieIntent,
    locale,
} = {}) {
    const { intent, source } = resolvePricingIntent({
        goal,
        category,
        cookieIntent,
    });

    const path = buildPath({ goal, category, intent, locale });
    const hero = HERO_KEYS[intent] || HERO_KEYS.discover;

    // Libellé injecté dans le titre du parcours : l'objectif exact quand on le
    // connaît (déjà traduit par l'onboarding), sinon le libellé de la famille.
    const goalLabelKey =
        (source === "goal" && getGoalLabelKey(goal)) || INTENT_LABEL_KEYS[intent];

    return {
        intent,
        source,
        goal: goal || null,
        goalLabelKey,
        heroTitleKey: hero.title,
        heroSubtitleKey: hero.subtitle,
        features: getPremiumFeatures(intent),
        path,
        // Le parcours de n8n tient en une seule formation : sans ce rappel, la
        // précision du bloc ferait paraître l'offre plus courte qu'elle n'est.
        // Le total suit le nombre annoncé partout ailleurs sur la page, pour ne
        // pas afficher deux comptes différents à quelques centimètres d'écart.
        otherCoursesCount: Math.max(getAnnouncedCatalogSize() - path.length, 0),
        // Le sélecteur disparaît seulement pour qui a déclaré un objectif
        // exploitable à l'onboarding. Il reste visible après un clic sur une
        // pastille (sinon, au rechargement, le choix ne serait plus modifiable)
        // et pour un « je ne sais pas », qui a pu se préciser depuis.
        showSelector: source !== "goal" || intent === "discover",
    };
}
